// BarWithAnnotation.tsx
import React from "react";
import { Bar } from "react-chartjs-2";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
// @ts-ignore
import * as utils from "../../../utils/utils.js";
ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	annotationPlugin,
	ChartDataLabels
);

type Props = {
	/** x-axis labels, one per bar */
	labels: string[];
	/** numeric values, one per bar (same length as labels) */
	values: number[];
	/**
	 * Which two bars to bracket: [leftIndex, rightIndex].
	 * Defaults to the last two bars.
	 */
	bracketBetween?: [number, number];
	/** Round percent to this many decimals (default 0) */
	percentDigits?: number;
	/** Optional override to style */
	barColor?: string;
	background?: string;
};

// const clamp = (n: number, min: number, max: number) =>
// 	Math.max(min, Math.min(max, n));

const BarWithAnnotation: React.FC<Props> = ({
	labels,
	values,
	// bracketBetween,
	// percentDigits = 0,
	barColor = "#FFD84C",
	background = "var(--surface-default)",
}) => {
	// --- Basics & guards
	const n = labels.length;
	const safeValues = values.map((v) => (Number.isFinite(v) ? v : 0));
	if (n === 0 || n !== safeValues.length) {
		return (
			<div className="text-red-600">
				Invalid data: labels and values must be non-empty and the same
				length.
			</div>
		);
	}

	// const [iLeft, iRight] = useMemo(() => {
	// 	if (
	// 		bracketBetween &&
	// 		Array.isArray(bracketBetween) &&
	// 		bracketBetween.length === 2
	// 	) {
	// 		const [a, b] = bracketBetween;
	// 		return [clamp(a, 0, n - 1), clamp(b, 0, n - 1)];
	// 	}
	// 	// default: last two bars
	// 	return [Math.max(0, n - 2), Math.max(0, n - 1)];
	// }, [bracketBetween, n]);

	// const leftLabel = labels[iLeft];
	// const rightLabel = labels[iRight];
	// const leftValue = safeValues[iLeft];
	// const rightValue = safeValues[iRight];

	// --- % change calculation (robust to zero/negatives)
	// const percentChange = useMemo(() => {
	// 	if (leftValue === 0)
	// 		return rightValue === 0 ? 0 : Infinity * Math.sign(rightValue);
	// 	const raw = ((rightValue - leftValue) / Math.abs(leftValue)) * 100;
	// 	// Handle very small numbers like -0
	// 	const rounded = Number.isFinite(raw)
	// 		? parseFloat(raw.toFixed(percentDigits))
	// 		: raw;
	// 	return rounded;
	// }, [leftValue, rightValue, percentDigits]);

	// const annotationText = useMemo(() => {
	// 	if (!Number.isFinite(percentChange)) {
	// 		// e.g., leftValue = 0, rightValue != 0
	// 		return rightValue === 0 ? "0%" : `${rightValue > 0 ? "+" : "–"}∞%`;
	// 	}
	// 	const sign = percentChange > 0 ? "+" : "";
	// 	return `${sign}${percentChange}%`;
	// }, [percentChange, rightValue]);

	// --- Layout helpers
	const maxVal = Math.max(...safeValues);
	const minVal = Math.min(...safeValues);
	const range = Math.max(1, maxVal - minVal);
	const pad = Math.max(0.1 * range, 0.6); // dynamic vertical padding for the bracket
	const yTop = maxVal + pad;

	const data = {
		labels,
		datasets: [
			{
				label: "Value",
				data: safeValues,
				backgroundColor: barColor,
				borderRadius: 8,
				hoverBackgroundColor: barColor,
				barThickness: 56,
			},
		],
	};

	const options: any = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			title: { display: false },
			tooltip: { enabled: true },
			datalabels: {
				color: "#111",
				font: { weight: "700" },
				formatter: (value: number) => utils.formatCost(value),
				anchor: "end",
				align: "end",
				clamp: true,
			},
			// annotation: {
			// 	annotations: {
			// 		leftBracket: {
			// 			type: "line",
			// 			xMin: leftLabel,
			// 			xMax: leftLabel,
			// 			yMin: leftValue,
			// 			yMax: yTop,
			// 			borderColor: "#111",
			// 			borderWidth: 2,
			// 		},
			// 		rightBracket: {
			// 			type: "line",
			// 			xMin: rightLabel,
			// 			xMax: rightLabel,
			// 			yMin: rightValue,
			// 			yMax: yTop,
			// 			borderColor: "#111",
			// 			borderWidth: 2,
			// 		},
			// 		topBracket: {
			// 			type: "line",
			// 			xMin: leftLabel,
			// 			xMax: rightLabel,
			// 			yMin: yTop,
			// 			yMax: yTop,
			// 			borderColor: "#111",
			// 			borderWidth: 2,
			// 		},
			// 		leftDot: {
			// 			type: "point",
			// 			xValue: leftLabel,
			// 			yValue: leftValue,
			// 			radius: 5,
			// 			backgroundColor: "#111",
			// 		},
			// 		rightDot: {
			// 			type: "point",
			// 			xValue: rightLabel,
			// 			yValue: rightValue,
			// 			radius: 5,
			// 			backgroundColor: "#111",
			// 		},
			// 		changeLabel: {
			// 			type: "label",
			// 			xValue: rightLabel,
			// 			yValue: yTop + range * 0.03, // a touch above the bracket
			// 			content: [annotationText],
			// 			callout: { display: false },
			// 			backgroundColor: "rgba(0,0,0,0)",
			// 			color: "#111",
			// 			font: { weight: "800", size: 16 },
			// 			textAlign: "center",
			// 			padding: 0,
			// 		},
			// 	},
			// },
		},
		scales: {
			x: {
				grid: { display: false },
				ticks: { color: "#333", font: { weight: "600" } },
			},
			y: {
				beginAtZero: minVal >= 0,
				suggestedMin: minVal < 0 ? minVal - pad * 0.3 : undefined,
				suggestedMax: yTop + pad * 0.5,
				grid: { color: "rgba(0,0,0,0.08)" },
				ticks: { precision: 0, stepSize: 1, color: "#555" },
				display: false,
			},
		},
	};

	return (
		<div
			className="w-full h-[360px] rounded-2xl p-4"
			style={{ background, minHeight: "300px" }}
		>
			<Bar data={data} options={options} />
		</div>
	);
};

export default BarWithAnnotation;
